import { Component, effect, inject, input, output } from '@angular/core'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { BndButton, BndFileUpload, BndFileUploadItem, BndModal, BndSelect } from '@bonduelle-it/storybook'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SelectOption } from '../../models'
import { requireDefined } from '../../utils/require-defined.util'
import { AoDocumentUploadOutput, AoDocumentUploadOutputList } from './ao-document-upload-output.interface'

export interface AoDocumentUploadExistingFile {
  id: number
  description?: string | null
  thumbnail?: string
  file?: File | null
  signedUrl?: string
  key?: string
  type?: string
}

export interface AoDocumentUploadData {
  title?: string
  allowedMimeTypes: string[]
  allowedExtensions: string[]
  maxFileSize: number
  maxFilesAllowed: number
  displayDescription: boolean
  existingFiles?: AoDocumentUploadExistingFile[] | null
  readOnlyMode?: boolean
  typeOptions?: string[]
}

@Component({
  selector: 'ao-document-upload',
  templateUrl: './ao-document-upload.component.html',
  imports: [TranslatePipe, ReactiveFormsModule, BndButton, BndModal, BndFileUpload, BndSelect],
})
export class AoDocumentUploadComponent {
  private readonly translateService = inject(TranslateService)

  public readonly open = input(false)
  public readonly data = input<AoDocumentUploadData>()
  public result = output<AoDocumentUploadOutputList | null | undefined>()

  // Copied from `data()` by the effect each time the dialog opens, before the dialog content renders
  public title?: string
  public accept = ''
  public hint = ''
  public maxFileSize!: number
  public maxFilesAllowed!: number
  public displayDescription!: boolean
  public readOnlyMode = false
  public typeOptions: SelectOption[] = []

  readonly files = new FormControl<BndFileUploadItem[]>([], { nonNullable: true })
  /** Type picked for each file (farm CSR), keyed by item id. */
  readonly types = new Map<string, FormControl<string | null>>()
  /** The file each listed item stands for, among the ones the dialog was opened with. */
  private existingFiles = new Map<string, AoDocumentUploadExistingFile>()

  constructor() {
    effect(() => {
      if (!this.open()) return
      const data = requireDefined(this.data(), 'the upload dialog data')
      this.title = data.title
      // Extensions too: some browsers give no MIME type for HEIC photos
      this.accept = [...data.allowedMimeTypes, ...data.allowedExtensions.map((ext) => `.${ext}`)].join(',')
      this.hint = this.buildHint(data)
      this.maxFileSize = data.maxFileSize
      this.maxFilesAllowed = data.maxFilesAllowed
      this.displayDescription = data.displayDescription
      this.readOnlyMode = data.readOnlyMode || false
      this.typeOptions = data.typeOptions?.map((type) => ({ label: this.typeLabel(type), value: type })) ?? []

      this.existingFiles = new Map()
      this.types.clear()
      const items = (data.existingFiles ?? []).map((file, index) => {
        const item = this.toItem(file, index)
        this.existingFiles.set(item.id, file)
        this.addTypeControl(item.id, file.type ?? null)
        return item
      })
      this.files.reset(items)
      if (this.readOnlyMode) {
        this.files.disable()
        this.types.forEach((control) => control.disable())
      } else {
        this.files.enable()
      }
    })
  }

  onFilesAdded(items: BndFileUploadItem[]): void {
    items.forEach((item) => this.addTypeControl(item.id, null))
  }

  onFileRemoved(item: BndFileUploadItem): void {
    this.types.delete(item.id)
  }

  onConfirm(): void {
    if (this.readOnlyMode) {
      this.result.emit(null)
    } else if (this.isFormValid()) {
      this.result.emit(this.files.value.map((item) => this.toOutput(item)))
    }
  }

  onCancel(): void {
    this.result.emit(null)
  }

  /** Every listed file needs a type when types are asked for. */
  isFormValid(): boolean {
    return this.readOnlyMode || [...this.types.values()].every((control) => control.valid)
  }

  private addTypeControl(id: string, type: string | null): void {
    if (this.typeOptions.length) {
      this.types.set(id, new FormControl<string | null>(type, Validators.required))
    }
  }

  private toItem(file: AoDocumentUploadExistingFile, index: number): BndFileUploadItem {
    // Intervention dialogs pass the stored documents themselves, which carry their MIME type
    const mimeType: string | undefined = file.file?.type || (file as { mimeType?: string }).mimeType
    const isImage = !!file.thumbnail || !!mimeType?.startsWith('image/')
    return {
      id: `existing-${index}`,
      name: file.description || file.file?.name || this.fallbackName(isImage, index),
      size: file.file?.size,
      type: mimeType ?? (isImage ? 'image/' : undefined),
      // Picked in a previous opening of the dialog: still waiting for the form to be saved
      status: file.file ? 'pending' : 'done',
      url: file.signedUrl,
      previewUrl: file.thumbnail ?? (isImage && !file.file ? file.signedUrl : undefined),
      file: file.file ?? undefined,
    }
  }

  private toOutput(item: BndFileUploadItem): AoDocumentUploadOutput {
    const typeControl = this.types.get(item.id)
    const existing = this.existingFiles.get(item.id)
    if (existing) {
      return {
        ...existing,
        description: existing.description ?? undefined,
        file: existing.file ?? null,
        ...(typeControl ? { type: typeControl.value ?? undefined } : {}),
      }
    }
    return {
      id: null as unknown as number,
      // The file name stands for the description, which the dialog used to prefill with it
      description: this.displayDescription ? item.name : undefined,
      file: requireDefined(item.file, 'the picked file'),
      ...(typeControl ? { type: typeControl.value ?? undefined } : {}),
    }
  }

  /** Translated label of a document type (`soil test` → `INPUTS.FILE.TYPE_OPTIONS.SOIL_TEST`), else the raw type. */
  private typeLabel(type: string): string {
    const key = `INPUTS.FILE.TYPE_OPTIONS.${type.toUpperCase().replace(/\s+/g, '_')}`
    const label: string = this.translateService.instant(key)
    return label === key ? type : label
  }

  private fallbackName(isImage: boolean, index: number): string {
    return `${this.translateService.instant(isImage ? 'INPUTS.FILE.PICTURE' : 'INPUTS.FILE.DOCUMENT')} ${index + 1}`
  }

  private buildHint(data: AoDocumentUploadData): string {
    const formats = data.allowedMimeTypes.map((type) => type.split('/')[1]).join(', ')
    const maxFileSize = this.translateService.instant('INPUTS.FILE.MAX_FILE_SIZE', {
      maxFileSize: data.maxFileSize / (1024 * 1024),
    })
    return `${this.translateService.instant('INPUTS.FILE.SUPPORTED_FORMATS')}${formats} · ${maxFileSize}`
  }
}
